import { lazy } from 'react';

import { createBrowserRouter, RouterProvider } from 'react-router';

import { AdminLayout } from '@/app/layouts/AdminLayout';
import { OrgSetupLayout } from '@/app/layouts/OrgSetupLayout';
import { ProtectedLayout } from '@/app/layouts/ProtectedLayout';
import { PublicLayout } from '@/app/layouts/PublicLayout';
import { ForgotPasswordPage } from '@/app/pages/auth/ForgotPasswordPage';
import { LoginPage } from '@/app/pages/auth/LoginPage';
import { OrgSetupPage } from '@/app/pages/auth/OrgSetupPage';
import { RegisterPage } from '@/app/pages/auth/RegisterPage';
import { ResetPasswordPage } from '@/app/pages/auth/ResetPasswordPage';
import { SocialCallbackPage } from '@/app/pages/auth/SocialCallbackPage';
import { VerifyEmailPage } from '@/app/pages/auth/VerifyEmailPage';
import { NotFoundPage } from '@/app/pages/NotFoundPage';
import { RootRedirect } from '@/app/pages/RootRedirect';
import { ComponentLibrary } from '@/lib/ui/_ComponentLibrary';
import { ErrorBoundary } from '@/lib/ui/organisms';
import { ROUTES } from '@/routes';

// ---------------------------------------------------------------------------
// Lazy-loaded pages — code-split per route. Auth pages are kept synchronous
// because they are the first paint for unauthenticated users.
// ---------------------------------------------------------------------------

const ActivityPage = lazy(() =>
  import('@/app/pages/activity/ActivityPage').then((m) => ({ default: m.ActivityPage })),
);
const AdminPage = lazy(() =>
  import('@/app/pages/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const AdminAllowedHostsPage = lazy(() =>
  import('@/app/pages/admin/allowed-hosts/AdminAllowedHostsPage').then((m) => ({
    default: m.AdminAllowedHostsPage,
  })),
);
const AdminApiKeysPage = lazy(() =>
  import('@/app/pages/admin/api-keys/AdminApiKeysPage').then((m) => ({
    default: m.AdminApiKeysPage,
  })),
);
const AdminApiUsagePage = lazy(() =>
  import('@/app/pages/admin/api-usage/AdminApiUsagePage').then((m) => ({
    default: m.AdminApiUsagePage,
  })),
);
const AdminDetectionsPage = lazy(() =>
  import('@/app/pages/admin/detections/AdminDetectionsPage').then((m) => ({
    default: m.AdminDetectionsPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/app/pages/admin/users/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AllowedHostsPage = lazy(() =>
  import('@/app/pages/allowed-hosts/AllowedHostsPage').then((m) => ({
    default: m.AllowedHostsPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import('@/app/pages/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
const ApiKeysPage = lazy(() =>
  import('@/app/pages/api-keys/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
);
const BillingPage = lazy(() =>
  import('@/app/pages/billing/BillingPage').then((m) => ({ default: m.BillingPage })),
);
const DashboardPage = lazy(() =>
  import('@/app/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const HowItWorksPage = lazy(() =>
  import('@/app/pages/how-it-works/HowItWorksPage').then((m) => ({ default: m.HowItWorksPage })),
);
const McpSessionsPage = lazy(() =>
  import('@/app/pages/mcp-sessions/McpSessionsPage').then((m) => ({
    default: m.McpSessionsPage,
  })),
);
const ModelCardDetailPage = lazy(() =>
  import('@/app/pages/model-cards/ModelCardDetailPage').then((m) => ({
    default: m.ModelCardDetailPage,
  })),
);
const ModelCardsPage = lazy(() =>
  import('@/app/pages/model-cards/ModelCardsPage').then((m) => ({ default: m.ModelCardsPage })),
);
const SettingsOrganizationPage = lazy(() =>
  import('@/app/pages/settings/SettingsOrganizationPage').then((m) => ({
    default: m.SettingsOrganizationPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/app/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const WebhookLogsPage = lazy(() =>
  import('@/app/pages/webhooks/WebhookLogsPage').then((m) => ({ default: m.WebhookLogsPage })),
);
const ThresholdsPage = lazy(() =>
  import('@/app/pages/admin/thresholds/ThresholdsPage').then((m) => ({
    default: m.ThresholdsPage,
  })),
);
const WebhooksPage = lazy(() =>
  import('@/app/pages/webhooks/WebhooksPage').then((m) => ({ default: m.WebhooksPage })),
);

// ---------------------------------------------------------------------------
// Application Router
// Public routes render under PublicLayout (no auth check).
// Org setup renders under OrgSetupLayout (signed in, no organization yet).
// Protected routes render under ProtectedLayout (redirects to /login if not authed,
// to /org-setup if the user has no organization).
// Admin routes render under AdminLayout (redirects to /dashboard if not admin).
// ---------------------------------------------------------------------------

const router = createBrowserRouter([
  // ── Public ──────────────────────────────────────────────────────────────
  {
    element: (
      <ErrorBoundary>
        <PublicLayout />
      </ErrorBoundary>
    ),
    children: [
      { path: ROUTES.login, element: <LoginPage /> },
      { path: ROUTES.register, element: <RegisterPage /> },
      { path: ROUTES.forgotPassword, element: <ForgotPasswordPage /> },
      { path: ROUTES.resetPassword, element: <ResetPasswordPage /> },
      { path: ROUTES.verifyEmail, element: <VerifyEmailPage /> },
      { path: ROUTES.socialCallback, element: <SocialCallbackPage /> },
    ],
  },

  // ── Signed in, no organization yet ─────────────────────────────────────
  {
    element: (
      <ErrorBoundary>
        <OrgSetupLayout />
      </ErrorBoundary>
    ),
    children: [{ path: ROUTES.orgSetup, element: <OrgSetupPage /> }],
  },

  // ── Protected ───────────────────────────────────────────────────────────
  {
    element: (
      <ErrorBoundary>
        <ProtectedLayout />
      </ErrorBoundary>
    ),
    children: [
      { path: ROUTES.dashboard, element: <DashboardPage /> },
      { path: ROUTES.apiKeys, element: <ApiKeysPage /> },
      { path: ROUTES.analytics, element: <AnalyticsPage /> },
      { path: ROUTES.billing, element: <BillingPage /> },
      { path: ROUTES.webhooks, element: <WebhooksPage /> },
      { path: ROUTES.webhookLogs, element: <WebhookLogsPage /> },
      { path: ROUTES.activity, element: <ActivityPage /> },
      { path: ROUTES.settings, element: <SettingsPage /> },
      { path: ROUTES.settingsOrganization, element: <SettingsOrganizationPage /> },
      { path: ROUTES.allowedHosts, element: <AllowedHostsPage /> },
      { path: ROUTES.mcpSessions, element: <McpSessionsPage /> },
      { path: ROUTES.modelCards, element: <ModelCardsPage /> },
      { path: ROUTES.modelCardDetail, element: <ModelCardDetailPage /> },
      { path: ROUTES.howItWorks, element: <HowItWorksPage /> },
    ],
  },

  // ── Admin only ──────────────────────────────────────────────────────────
  {
    element: (
      <ErrorBoundary>
        <AdminLayout />
      </ErrorBoundary>
    ),
    children: [
      { path: ROUTES.admin, element: <AdminPage /> },
      { path: ROUTES.adminUsers, element: <AdminUsersPage /> },
      { path: ROUTES.adminApiKeys, element: <AdminApiKeysPage /> },
      { path: ROUTES.adminApiUsage, element: <AdminApiUsagePage /> },
      { path: ROUTES.adminDetections, element: <AdminDetectionsPage /> },
      { path: ROUTES.adminAllowedHosts, element: <AdminAllowedHostsPage /> },
      { path: ROUTES.adminThresholds, element: <ThresholdsPage /> },
    ],
  },

  // ── Dev only ────────────────────────────────────────────────────────────
  ...(import.meta.env.DEV ? [{ path: '/dev/components', element: <ComponentLibrary /> }] : []),

  // ── Root ────────────────────────────────────────────────────────────────
  { path: '/', element: <RootRedirect /> },

  // ── Catch-all ───────────────────────────────────────────────────────────
  { path: '*', element: <NotFoundPage /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
